package com.islamicmedia.shared.data.cache

import com.islamicmedia.shared.data.auth.SupabaseAuthService
import io.github.jan.supabase.postgrest.from
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

// ---------------------------------------------------------------------------
// Row types for admin dashboard queries
// ---------------------------------------------------------------------------

@Serializable
data class AdminMediaRow(
    @SerialName("cache_key") val cacheKey: String = "",
    val title: String = "",
    @SerialName("media_type") val mediaType: String = "",
    @SerialName("hit_count") val hitCount: Int = 0,
    @SerialName("created_at") val createdAt: String? = null
) {
    /** Title with year extracted from cache_key (e.g. "The Lion King (1994)") */
    val displayTitle: String get() {
        val parts = cacheKey.split("::")
        val year = parts.getOrNull(2)?.takeIf { it.isNotBlank() }
        return if (year != null) "$title ($year)" else title
    }
}

@Serializable
data class AdminEventRow(
    val id: Long? = null,   // analytics_events.id is a numeric (bigint) id, not a UUID
    @SerialName("event_type") val eventType: String = "",
    val title: String? = null,
    @SerialName("media_type") val mediaType: String? = null,
    @SerialName("user_id") val userId: String? = null,
    @SerialName("user_email") val userEmail: String? = null,
    val score: Int? = null,
    @SerialName("islamic_score") val islamicScore: Int? = null,
    @SerialName("concern_categories") val concernCategories: List<String>? = null,
    @SerialName("criteria_json") val criteriaJson: String? = null,
    @SerialName("result_json") val resultJson: String? = null,
    @SerialName("cache_layer") val cacheLayer: String? = null,
    @SerialName("cost_usd") val costUsd: Double? = null,
    @SerialName("cost_breakdown_json") val costBreakdownJson: String? = null,
    @SerialName("duration_seconds") val durationSeconds: Int? = null,
    @SerialName("created_at") val createdAt: String? = null,
    // Lightweight quick/full flag. The dashboard reads this instead of the heavy
    // result_json (which is now loaded lazily, per-row, only on expand).
    @SerialName("quick_check") val quickCheck: Boolean? = null,
    // Where THIS search was run, stamped at insert time (server sniffs the User-Agent;
    // app_version comes from the X-App-Version header). Null when the UA didn't match,
    // and app_version is null on web and pre-header builds.
    val platform: String? = null,
    @SerialName("app_version") val appVersion: String? = null,
    // How this search was initiated (autocomplete/disambiguation/photo/isbn/recommendation
    // rows/upgrade). Null on legacy rows + pre-release clients → shown as "Unknown".
    @SerialName("search_source") val searchSource: String? = null
) {
    /** Prefer the light quick_check column; fall back to result_json if present. */
    val isQuickCheck: Boolean get() = quickCheck == true || resultJson?.contains("\"quickCheck\":true") == true
}

/**
 * "Top searched media" ranked by actual analysis count (analytics events), so the number
 * matches the per-title "Times Searched" drill-in. Groups by the event's display title (which
 * already carries the year) + media type. Replaces the old media_content.hit_count ranking,
 * which counted cache reuse and never reconciled with the event count. Pass the SAME filtered
 * event list the rest of the dashboard uses so the admin-filter toggle is respected.
 */
fun topSearchedMedia(events: List<AdminEventRow>, limit: Int = 20): List<AdminMediaRow> =
    events.filter { !it.title.isNullOrBlank() }
        .groupBy { (it.title ?: "") to (it.mediaType ?: "") }
        .map { (key, evs) ->
            AdminMediaRow(
                cacheKey = "",                 // year is already part of the event title
                title = key.first,
                mediaType = key.second,
                hitCount = evs.size,           // now: number of searches, not cache reuse
                createdAt = evs.minByOrNull { it.createdAt ?: "" }?.createdAt
            )
        }
        .sortedByDescending { it.hitCount }
        .take(limit)

// ---------------------------------------------------------------------------
// Drill-down summary types
// ---------------------------------------------------------------------------

data class UserSummary(
    val userId: String?,
    val userEmail: String?,
    val totalSearches: Int,
    val lastSearchAt: String?,
    val avgScore: Double?
)

data class MediaTypeSummary(
    val mediaType: String,
    val totalSearches: Int,
    val uniqueTitles: Int,
    val avgScore: Double?,
    val avgIslamicScore: Double?
)

// ---------------------------------------------------------------------------
// User Journey row type
// ---------------------------------------------------------------------------

@Serializable
data class UserJourneyRow(
    val id: String? = null,
    @SerialName("user_id") val userId: String? = null,
    @SerialName("user_email") val userEmail: String? = null,
    @SerialName("search_query") val searchQuery: String = "",
    @SerialName("disambiguation_count") val disambiguationCount: Int = 0,
    @SerialName("selected_title") val selectedTitle: String? = null,
    @SerialName("selected_media_type") val selectedMediaType: String? = null,
    @SerialName("selected_year") val selectedYear: String? = null,
    @SerialName("analysis_log_id") val analysisLogId: String? = null,
    val status: String = "searched",
    @SerialName("created_at") val createdAt: String? = null,
    @SerialName("selected_at") val selectedAt: String? = null,
    @SerialName("completed_at") val completedAt: String? = null
)

@Serializable
data class AdminEmailRow(val email: String = "")

// ---------------------------------------------------------------------------
// Dashboard aggregate returned to the UI layer
// ---------------------------------------------------------------------------

data class DashboardData(
    val totalAnalyses: Int = 0,
    val uniqueTitles: Int = 0,
    val uniqueUsers: Int = 0,
    val cacheHitRate: Double = 0.0,
    val avgDuration: Double = 0.0,
    val topMedia: List<AdminMediaRow> = emptyList(),
    val recentAnalyses: List<AdminEventRow> = emptyList(),
    val allAnalyses: List<AdminEventRow> = emptyList(),
    val userSummaries: List<UserSummary> = emptyList(),
    val mediaTypeSummaries: List<MediaTypeSummary> = emptyList(),
    val scoreSafe: Int = 0,
    val scoreCaution: Int = 0,
    val scoreNotSafe: Int = 0,
    val userJourneys: List<UserJourneyRow> = emptyList(),
    val adminEmails: List<String> = emptyList()
)

/**
 * Does [email] match the admin allowlist? An entry that starts with "@" is a DOMAIN rule —
 * "@grr.la" matches every address at that domain, which is how the throwaway domains used for test
 * accounts are excluded without listing each address. Anything else is an exact address.
 *
 * ANALYTICS EXCLUSION ONLY. Admin API access (AdminRoutes.checkAdminAccess) deliberately stays
 * exact-match: grr.la and sharklasers.com are public disposable-mail domains, so treating a domain
 * rule as "is an admin" there would hand the dashboard to anyone who can click "get a new address".
 */
fun matchesAdminAllowlist(email: String?, allowlist: Collection<String>): Boolean {
    val e = email?.trim()?.lowercase()?.takeIf { it.isNotEmpty() } ?: return false
    return allowlist.any { raw ->
        val entry = raw.trim().lowercase()
        when {
            entry.isEmpty() -> false
            entry.startsWith("@") -> e.endsWith(entry)
            else -> e == entry
        }
    }
}

// ---------------------------------------------------------------------------
// Service that fetches all dashboard data via Supabase Postgrest
// ---------------------------------------------------------------------------

class AnalyticsService {

    private val client = SupabaseAuthService.instance.client

    val isLoggedIn: Boolean
        get() = SupabaseAuthService.instance.isLoggedIn

    suspend fun fetchAdminEmails(): List<String> = try {
        client.from("admin_emails").select().decodeList<AdminEmailRow>().map { it.email.lowercase() }
    } catch (_: Exception) { emptyList() }

    // PostgREST caps EVERY response at the project's max-rows (1000 by default), so a single
    // request can never return more than 1000 rows no matter how large a limit() we ask for — that
    // cap is why the dashboard's journey count sat pinned at 1000. Page through with range() (each
    // page is within the cap) until a short page comes back, so the count and the funnel breakdown
    // reflect ALL journeys. Newest-first so an early safety-cap break keeps the most recent data.
    suspend fun fetchJourneys(): List<UserJourneyRow> = try {
        val pageSize = 1000
        val all = mutableListOf<UserJourneyRow>()
        var from = 0L
        while (true) {
            val page = client.from("user_journeys").select {
                order("created_at", io.github.jan.supabase.postgrest.query.Order.DESCENDING)
                range(from, from + pageSize - 1)
            }.decodeList<UserJourneyRow>()
            all.addAll(page)
            if (page.size < pageSize || all.size >= 100_000) break  // last page, or safety cap
            from += pageSize
        }
        all
    } catch (_: Exception) { emptyList() }

    suspend fun fetchMedia(): List<AdminMediaRow> = try {
        // PostgREST caps every response at the project's max-rows (1000), so paginate until done —
        // same pattern as fetchJourneys(). Without this the count was pinned at ~610.
        val pageSize = 1000
        val all = mutableListOf<AdminMediaRow>()
        var from = 0L
        while (true) {
            val page = client.from("media_content").select(
                io.github.jan.supabase.postgrest.query.Columns.list(
                    "cache_key", "title", "media_type", "hit_count", "created_at"
                )
            ) {
                order("created_at", io.github.jan.supabase.postgrest.query.Order.ASCENDING)
                range(from, from + pageSize - 1)
            }.decodeList<AdminMediaRow>()
            all.addAll(page)
            if (page.size < pageSize || all.size >= 100_000) break
            from += pageSize
        }
        all
    } catch (_: Exception) { emptyList() }

    // Pull ONLY the scalar columns the dashboard needs — NOT the heavy per-row result_json (10–50 KB/row;
    // at 50k rows that OOMs the server). Expanded per-row detail loads lazily via /api/admin/event-result.
    // Filtered to completed analyses (the set the dashboard counts).
    suspend fun fetchEvents(): List<AdminEventRow> = try {
        // PostgREST caps every response at max-rows (1000) regardless of limit() — that cap is why
        // Total Analyses sat pinned at 1000. Page with range() like fetchJourneys()/fetchMedia().
        // event_type filtered server-side so we only page the rows the dashboard actually counts.
        val pageSize = 1000
        val all = mutableListOf<AdminEventRow>()
        var from = 0L
        while (true) {
            val page = client.from("analytics_events").select(
                io.github.jan.supabase.postgrest.query.Columns.list(
                    "id", "event_type", "title", "media_type", "user_id", "user_email",
                    "score", "islamic_score", "concern_categories", "criteria_json", "quick_check",
                    "cache_layer", "cost_usd", "duration_seconds", "created_at",
                    "platform", "app_version", "search_source"
                )
            ) {
                filter { eq("event_type", "analysis_complete") }
                order("created_at", io.github.jan.supabase.postgrest.query.Order.DESCENDING)
                range(from, from + pageSize - 1)
            }.decodeList<AdminEventRow>()
            all.addAll(page)
            if (page.size < pageSize || all.size >= 100_000) break  // last page, or safety cap
            from += pageSize
        }
        all
    } catch (_: Exception) { emptyList() }

    /** Fetches all four sources CONCURRENTLY (slowest query gates the load, not their sum) and
     *  assembles them. The admin UI fetches the pieces separately for progressive, per-section render. */
    suspend fun fetchDashboardData(): DashboardData = coroutineScope {
        val a = async { fetchAdminEmails() }
        val j = async { fetchJourneys() }
        val e = async { fetchEvents() }
        val m = async { fetchMedia() }
        buildDashboardData(e.await(), m.await(), j.await(), a.await())
    }
}

/**
 * Pure builder — derives the dashboard aggregate from already-fetched pieces, so the UI can fetch
 * the pieces in parallel and rebuild the view as each one arrives. [analyses] must already be the
 * completed-analysis events (see [AnalyticsService.fetchEvents]).
 */
fun buildDashboardData(
    analyses: List<AdminEventRow>,
    media: List<AdminMediaRow>,
    journeys: List<UserJourneyRow>,
    adminEmails: List<String>
): DashboardData {
    val topMedia = topSearchedMedia(analyses)

    // Cache-hit rate: the first analysis of each distinct title is a fresh computation (miss);
    // every repeat is a cache hit. Derived from analytics_events (the only client-readable source).
    val distinctAnalyzed = analyses.map { "${it.title ?: ""}::${it.mediaType ?: ""}" }.toSet().size
    val cacheHitRate = if (analyses.isNotEmpty())
        (analyses.size - distinctAnalyzed).toDouble() / analyses.size else 0.0
    val avgDuration = if (analyses.isNotEmpty()) {
        analyses.mapNotNull { it.durationSeconds }.map { it.toDouble() }.average().takeIf { !it.isNaN() } ?: 0.0
    } else 0.0

    val safe = analyses.count { (it.score ?: 0) in 7..10 }
    val caution = analyses.count { (it.score ?: 0) in 4..6 }
    val notSafe = analyses.count { (it.score ?: 0) in 1..3 }
    val recent = analyses.sortedByDescending { it.createdAt ?: "" }.take(20)

    val userGroups = analyses.groupBy { it.userEmail?.takeIf { e -> e.isNotBlank() } ?: it.userId }
    val userSummaries = userGroups.map { (_, events) ->
        val scores = events.mapNotNull { it.score }
        UserSummary(
            userId = events.firstOrNull()?.userId,
            userEmail = events.firstOrNull()?.userEmail?.takeIf { it.isNotBlank() },
            totalSearches = events.size,
            lastSearchAt = events.maxByOrNull { it.createdAt ?: "" }?.createdAt,
            avgScore = if (scores.isNotEmpty()) scores.average() else null
        )
    }.sortedByDescending { it.totalSearches }
    val uniqueUsers = userGroups.keys.count { it != null }

    val typeGroups = analyses.groupBy { it.mediaType ?: "unknown" }
    val mediaTypeSummaries = typeGroups.map { (type, events) ->
        val scores = events.mapNotNull { it.score }
        val islamicScores = events.mapNotNull { it.islamicScore }
        MediaTypeSummary(
            mediaType = type,
            totalSearches = events.size,
            uniqueTitles = events.mapNotNull { it.title }.distinct().size,
            avgScore = if (scores.isNotEmpty()) scores.average() else null,
            avgIslamicScore = if (islamicScores.isNotEmpty()) islamicScores.average() else null
        )
    }.sortedByDescending { it.totalSearches }

    return DashboardData(
        totalAnalyses = analyses.size,
        uniqueTitles = media.map { it.title }.distinct().size,
        uniqueUsers = uniqueUsers,
        cacheHitRate = cacheHitRate,
        avgDuration = avgDuration,
        topMedia = topMedia,
        recentAnalyses = recent,
        allAnalyses = analyses.sortedByDescending { it.createdAt ?: "" },
        userSummaries = userSummaries,
        mediaTypeSummaries = mediaTypeSummaries,
        scoreSafe = safe,
        scoreCaution = caution,
        scoreNotSafe = notSafe,
        userJourneys = journeys,
        adminEmails = adminEmails
    )
}
